import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { programmeService } from '../services/programme'
import type { ProgrammeFilterParams, PaginationParams, CreateProgramme, UpdateProgramme } from '../types'

// Query keys for programme management
export const programmeKeys = {
  all: ['programme'] as const,
  programmes: () => [...programmeKeys.all, 'programmes'] as const,
  programme: (id: string) => [...programmeKeys.programmes(), id] as const,
  programmesPaginated: (params: PaginationParams, filters: ProgrammeFilterParams) =>
    [...programmeKeys.programmes(), 'paginated', params, filters] as const,
  stats: () => [...programmeKeys.all, 'stats'] as const
}


export const useProgrammes = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: ProgrammeFilterParams = {}
) => {
  return useQuery({
    queryKey: programmeKeys.programmesPaginated(params, filters),
    queryFn: () => programmeService.getProgrammes(params, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData
  })
}


export const useProgramme = (programmeId: string) => {
  return useQuery({
    queryKey: programmeKeys.programme(programmeId),
    queryFn: () => programmeService.getProgrammeById(programmeId),
    enabled: !!programmeId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}


export const useProgrammeStats = () => {
  return useQuery({
    queryKey: programmeKeys.stats(),
    queryFn: async () => {
      const [draftResponse, publishedResponse] = await Promise.all([
        programmeService.getTotalDraftProgrammes(),
        programmeService.getTotalPublishedProgrammes()
      ])

      console.log("Draft response and published response",draftResponse, publishedResponse)

      return {
        draft: draftResponse.data || 0,
        published: publishedResponse.data || 0,
        total: 0
      }
    },
    staleTime: 5 * 60 * 1000
  })
}


export const useCreateProgramme = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateProgramme) => programmeService.createProgramme(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: programmeKeys.programmes() })
      queryClient.invalidateQueries({ queryKey: programmeKeys.stats() })
    },
    onError: (error) => {
      console.error('Programme creation failed:', error)
    }
  })
}


export const useUpdateProgramme = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ programmeId, data }: { programmeId: string; data: UpdateProgramme }) =>
      programmeService.updateProgramme(programmeId, data),
    onSuccess: (_response, { programmeId }) => {
      queryClient.invalidateQueries({ queryKey: programmeKeys.programme(programmeId) })
      queryClient.invalidateQueries({ queryKey: programmeKeys.programmes() })
    },
    onError: (error) => {
      console.error('Programme update failed:', error)
    }
  })
}


export const useUpdateProgrammeStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ programmeId, status }: { programmeId: string; status: 'DRAFT' | 'PUBLISH' }) =>
      programmeService.updateProgrammeStatus(programmeId, status),
    onSuccess: (response, { programmeId }) => {
      if (response.success && response.data) {
        queryClient.setQueryData(programmeKeys.programme(programmeId), response.data)
      }

      queryClient.invalidateQueries({ queryKey: programmeKeys.programmes() })
      queryClient.invalidateQueries({ queryKey: programmeKeys.stats() })
    },
    onError: (error) => {
      console.error('Programme status update failed:', error)
    }
  })
}


export const useDeleteProgramme = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (programmeId: string) => programmeService.deleteProgramme(programmeId),
    onSuccess: (_, programmeId) => {
      queryClient.removeQueries({ queryKey: programmeKeys.programme(programmeId) })
      queryClient.invalidateQueries({ queryKey: programmeKeys.programmes() })
      queryClient.invalidateQueries({ queryKey: programmeKeys.stats() })
    },
    onError: (error) => {
      console.error('Programme deletion failed:', error)
    }
  })
}
